import { useState } from "react";
import { ShoppingCart, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { money, productImage } from "../../lib/api";
import { MobileSection, MobileStickyBar } from "./MobileSection";
import { MadeInUSABadge } from "../MadeInUSABadge";
import { SeoProse, cleanDescription } from "../SeoProse";

// Phone composition of the product page. Receives the exact same JSX blocks
// the desktop layout renders, so behaviour and state are shared 1:1.
export function MobileProductLayout({
  product, gallery, activeImage, setActiveImage,
  price, calcing, quantity, onAddToCart, hasAddonsOrTurnaround, blocks, isWedding,
}) {
  const [descOpen, setDescOpen] = useState(false);
  const total = price?.line_total ?? null;
  const unit = price?.unit_price ?? null;
  const pageBgClass = isWedding ? "bg-[#FBF8F3]" : "bg-slate-50";
  const cardClass = isWedding
    ? "bg-white border border-[#E8D9C3] rounded-none"
    : "card-base";

  return (
    <main className={`${pageBgClass} min-h-screen pb-36`} data-testid="product-detail-page">
      <div className="[&>div]:py-3 [&>div]:truncate">{blocks.breadcrumbs}</div>

      <div className="px-3 space-y-3">
        <div className={`${cardClass} overflow-hidden`} data-testid="mobile-gallery">
          <div className="aspect-[16/10] bg-slate-100">
            <img src={productImage(gallery[activeImage])} alt={product.name} className="w-full h-full object-cover" data-testid="product-hero-image" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 px-3 py-2 overflow-x-auto scrollbar-none border-t border-slate-100" data-testid="image-thumbs">
              {gallery.map((g, i) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setActiveImage(i)}
                  className={`w-14 h-14 shrink-0 overflow-hidden border-2 ${i === activeImage ? "border-[#333F48]" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={productImage(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="p-4">
            <div className="flex flex-wrap gap-1.5 mb-2">
              <span className="badge-navy">{product.category}</span>
              {product.is_featured && <span className="badge-accent">Featured</span>}
              <MadeInUSABadge />
            </div>
            <h1 className="font-display text-2xl font-black text-slate-900 tracking-tight leading-tight mb-2">{product.name}</h1>
            <div className={`relative ${descOpen ? "" : "max-h-[4.6rem] overflow-hidden"}`} data-testid="mobile-description">
              <SeoProse text={cleanDescription(product.description, product.name)} className="text-sm text-slate-600" />
              {!descOpen && <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />}
            </div>
            {(product.description || "").length > 160 && (
              <button
                type="button"
                onClick={() => setDescOpen((v) => !v)}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-kolt-orange"
                data-testid="mobile-desc-toggle"
              >
                {descOpen ? "Show less" : "Read more"}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${descOpen ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        </div>

        <MobileSection step="1" title="Specification" summary="Material, size, shape, colour and quantity" testId="mobile-section-spec">
          {blocks.specBlock}
        </MobileSection>

        <div className={`${cardClass} p-4`} data-testid="mobile-price-card">
          {blocks.priceBlock}
          {blocks.shippingBlock}
        </div>

        {hasAddonsOrTurnaround && (
          <MobileSection step="2" title="Add-ons & turnaround" summary="Optional extras and rush production" defaultOpen={false} testId="mobile-section-addons">
            <div className="-mt-5">{blocks.addonsBlock}</div>
          </MobileSection>
        )}

        <MobileSection step={hasAddonsOrTurnaround ? "3" : "2"} title="Artwork" summary="Upload print-ready files or ask a designer" defaultOpen={false} testId="mobile-section-artwork">
          {blocks.artworkBlock}
        </MobileSection>

        <MobileSection step={hasAddonsOrTurnaround ? "4" : "3"} title="Production notes" summary="Special instructions for your designer" defaultOpen={false} testId="mobile-section-notes">
          <div className="-mt-5">{blocks.notesBlock}</div>
        </MobileSection>

        <div className={`${cardClass} p-4 [&>div:first-child]:mt-0`}>{blocks.actionsBlock}</div>

        {blocks.stockGuide}
        {blocks.seoBody}
        <div className="[&_[role=tablist]]:overflow-x-auto [&_[role=tablist]]:px-3 [&_[role=tablist]]:scrollbar-none">{blocks.tabsCard}</div>
      </div>

      <MobileStickyBar testId="pdp-mobile-bar">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-500">
            {calcing ? "Calculating…" : total != null ? `${Number(quantity || 0).toLocaleString()} units` : "Configure to see price"}
          </div>
          <div className="font-display font-black text-xl text-[#333F48] leading-none" data-testid="pdp-mobile-total">
            {total != null ? money(total) : "—"}
            {unit != null && <span className="text-[11px] text-slate-500 font-semibold ml-1.5">{money(unit)} / unit</span>}
          </div>
        </div>
        <Button onClick={onAddToCart} className="btn-primary px-5" disabled={!price} data-testid="add-to-cart-btn-mobile">
          <ShoppingCart className="w-4 h-4" /> Add to Cart
        </Button>
      </MobileStickyBar>
    </main>
  );
}
